import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { ThemePreference } from "@tw-pulse/shared";
import { api } from "./api";

type Theme = ThemePreference;
type Resolved = "light" | "dark";

const STORAGE_KEY = "tw-pulse.theme";

interface ThemeContextValue {
  /** The user-selected preference, including "system". */
  theme: Theme;
  /** The currently rendered theme — "system" is resolved to light|dark. */
  resolved: Resolved;
  /** Update the preference. Writes localStorage immediately and fires a
   *  fire-and-forget PATCH to the server. The server call is silent — UI
   *  doesn't wait for it. */
  setTheme: (next: Theme) => void;
  /** Adopt a server-provided preference once /me resolves. Only applied when
   *  the user has no local override (i.e. localStorage is empty). */
  adoptFromServer: (server: Theme | null | undefined) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
    return null;
  } catch {
    return null;
  }
}

function writeStored(next: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore — incognito / quota */
  }
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolve(theme: Theme): Resolved {
  if (theme === "system") return systemPrefersDark() ? "dark" : "light";
  return theme;
}

function applyToDom(resolved: Resolved): void {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Boot: the FOUC script already applied a class. We derive the initial
  // preference from localStorage (or "system" if absent) to keep them in sync.
  const [theme, setThemeState] = useState<Theme>(() => readStored() ?? "system");
  const [resolved, setResolved] = useState<Resolved>(() => resolve(theme));

  // True once the server hydration has been processed — prevents the server
  // value from overwriting an in-session user toggle.
  const serverAdoptedRef = useRef(false);

  // Apply DOM class whenever the resolved theme changes.
  useEffect(() => {
    applyToDom(resolved);
  }, [resolved]);

  // React to system preference flips while in "system" mode.
  useEffect(() => {
    if (theme !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => setResolved(systemPrefersDark() ? "dark" : "light");
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    writeStored(next);
    setThemeState(next);
    setResolved(resolve(next));
    serverAdoptedRef.current = true; // user choice wins from now on
    // Fire-and-forget sync. Failure is non-fatal — local pref still applies.
    api.users.updatePreferences({ theme: next }).catch(() => void 0);
  }, []);

  const adoptFromServer = useCallback((server: Theme | null | undefined) => {
    if (serverAdoptedRef.current) return; // user already chose this session
    serverAdoptedRef.current = true;
    if (!server) return;
    // Server wins only when there's no local override.
    if (readStored() != null) return;
    writeStored(server);
    setThemeState(server);
    setResolved(resolve(server));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolved, setTheme, adoptFromServer }),
    [theme, resolved, setTheme, adoptFromServer],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
